/**
 * Required-field schemas for each import `kind`.
 *
 * Aliases were curated from real-world EV/ICE tracker exports
 * (EEVEE, Teslafi, A Better Routeplanner, Fuelio, Drivvo) plus common
 * spreadsheet variants. Add new aliases here — no other file changes needed.
 */
import type { RequiredFieldSchema } from "./types";

// Note: aliases are matched case-insensitively and after stripping common
// punctuation/units (see headerMapper.ts). Exact-match aliases come first
// so they outrank looser substring matches.

export const TRIP_SCHEMA: RequiredFieldSchema = {
  kind: "trip",
  label: "Trip",
  fields: [
    {
      key: "date",
      label: "Date",
      type: "date",
      required: true,
      aliases: ["date", "trip date", "started at", "start time", "start date", "departed at", "datetime", "day"],
    },
    {
      key: "distance",
      label: "Distance",
      type: "number",
      required: true,
      aliases: ["distance", "trip distance", "miles", "km", "kilometers", "kilometres", "length"],
      derive: {
        from: ["start_odometer", "end_odometer"],
        description: "Calculated from odometer start → end",
      },
    },
    {
      key: "unit",
      label: "Unit",
      type: "string",
      required: false,
      aliases: ["unit", "distance unit", "units"],
    },
    {
      key: "notes",
      label: "Notes",
      type: "string",
      required: false,
      aliases: ["notes", "note", "comment", "comments", "description", "memo"],
    },
    {
      key: "start_odometer",
      label: "Start odometer (km)",
      type: "number",
      required: false,
      aliases: ["odometer start", "start odometer", "odo start", "starting odometer", "odometer from"],
    },
    {
      key: "end_odometer",
      label: "End odometer (km)",
      type: "number",
      required: false,
      aliases: ["odometer end", "end odometer", "odo end", "ending odometer", "odometer to", "finished at odometer"],
    },
    {
      key: "start_battery_pct",
      label: "Start battery %",
      type: "percent",
      required: false,
      aliases: ["percentage start", "start battery", "battery start", "soc start", "starting soc", "start %"],
    },
    {
      key: "end_battery_pct",
      label: "End battery %",
      type: "percent",
      required: false,
      aliases: ["percentage end", "end battery", "battery end", "soc end", "ending soc", "end %"],
    },
  ],
};

export const CHARGING_SCHEMA: RequiredFieldSchema = {
  kind: "charging",
  label: "Charging session",
  fields: [
    {
      key: "date",
      label: "Date",
      type: "date",
      required: true,
      aliases: ["date", "session date", "started at", "start time", "charge date", "plugged in at"],
    },
    {
      key: "amount",
      label: "Energy (kWh)",
      type: "number",
      required: true,
      aliases: ["kwh", "energy", "amount", "energy used", "energy added", "added energy", "session energy"],
    },
    {
      key: "cost",
      label: "Cost",
      type: "number",
      required: false,
      aliases: ["cost", "total cost", "total", "price", "charge cost", "session cost"],
    },
    {
      key: "price_per_unit",
      label: "Price per kWh",
      type: "number",
      required: false,
      aliases: ["price per kwh", "$/kwh", "rate", "unit price", "kwh price"],
    },
    {
      key: "odometer",
      label: "Odometer (km)",
      type: "number",
      required: false,
      aliases: ["odometer", "odo", "mileage at charge"],
    },
    {
      key: "start_battery_pct",
      label: "Start battery %",
      type: "percent",
      required: false,
      aliases: ["percentage start", "start battery", "battery start", "soc start", "start %"],
    },
    {
      key: "end_battery_pct",
      label: "End battery %",
      type: "percent",
      required: false,
      aliases: ["percentage end", "end battery", "battery end", "soc end", "end %"],
    },
    {
      key: "notes",
      label: "Notes",
      type: "string",
      required: false,
      aliases: ["notes", "note", "location", "station", "comment"],
    },
  ],
};

export const FUEL_SCHEMA: RequiredFieldSchema = {
  kind: "fuel",
  label: "Fuel fill-up",
  fields: [
    {
      key: "date",
      label: "Date",
      type: "date",
      required: true,
      aliases: ["date", "fill date", "fuel date", "started at", "purchase date"],
    },
    {
      key: "amount",
      label: "Amount",
      type: "number",
      required: true,
      aliases: ["liters", "litres", "gallons", "amount", "volume", "quantity", "fuel"],
    },
    {
      key: "unit",
      label: "Unit",
      type: "string",
      required: false,
      aliases: ["unit", "fuel unit", "volume unit"],
    },
    {
      key: "cost",
      label: "Cost",
      type: "number",
      required: false,
      aliases: ["cost", "total cost", "total", "price", "fuel cost"],
    },
    {
      key: "price_per_unit",
      label: "Price per unit",
      type: "number",
      required: false,
      aliases: ["price per liter", "price per litre", "price per gallon", "$/l", "$/gal", "rate", "unit price"],
    },
    {
      key: "odometer",
      label: "Odometer (km)",
      type: "number",
      required: false,
      aliases: ["odometer", "odo", "mileage"],
    },
    {
      key: "notes",
      label: "Notes",
      type: "string",
      required: false,
      aliases: ["notes", "note", "station", "comment", "location"],
    },
  ],
};

export const SCHEMA_BY_KIND: Record<string, RequiredFieldSchema> = {
  trip: TRIP_SCHEMA,
  charging: CHARGING_SCHEMA,
  fuel: FUEL_SCHEMA,
};
